import {Tooltip} from 'platform/components';
import {Text} from 'platform/foundation';
import {CurrencyFormat, useFormatCurrency} from 'platform/locale';

import {isNil, join, pipe, split} from 'ramda';
import {isNotNilOrEmpty, mapIndexed} from 'ramda-adjunct';

import {CellRendererProps} from '../../types/CellRendererProps';
import {CellStyle} from '../../types/CellStyle';
import {TooltipGetter} from '../../types/TooltipGetter';
import {getNumericCellClassName} from '../../utils/getCellClassName';
import {getStylesToRender} from '../../utils/getStylesToRender';
import {getTooltipProps} from '../../utils/getTooltipProps';
import {EmptyCellValue} from '../EmptyCellValue/EmptyCellValue';

type MoneyValue = {amount: number; currency: string} | null;

type MoneyWithoutDecimalCellRendererProps = CellRendererProps<MoneyValue> & {
  getTooltipProps?: TooltipGetter;
};

const removeDecimal = (val: string, index: number): string => {
  if (index === 1) {
    return val.slice(2);
  }
  return val;
};

const formatMoney = pipe<string[], [string, string], [string, string], string>(
  // @ts-ignore
  split(','),
  mapIndexed(removeDecimal),
  join(' ')
);

export function MoneyWithoutDecimalCellRenderer(props: MoneyWithoutDecimalCellRendererProps) {
  const className = getNumericCellClassName(props.isWrap);
  const cellStyles = getStylesToRender<CellStyle>(props);
  const formatCurrency = useFormatCurrency();
  const tooltipProps = getTooltipProps(props.row, props.getTooltipProps);

  if (isNil(props.value)) {
    return <EmptyCellValue emptyCellValue={props.emptyCellValue} isWrap={props.isWrap} isNumeric />;
  }

  const value = isNotNilOrEmpty(props.value.amount)
    ? formatCurrency(props.value.amount, props.value.currency, 2)
    : null;

  if (isNil(value)) {
    return <EmptyCellValue emptyCellValue={props.emptyCellValue} isWrap={props.isWrap} isNumeric />;
  }

  const formattedMoney = formatMoney(value) as string;

  return (
    <Tooltip {...tooltipProps} label={formattedMoney}>
      <div className={className}>
        <Text
          color={cellStyles?.textColor}
          noWrap={!props.isWrap}
          size="small"
          overflowWrap={props.isWrap ? 'anywhere' : undefined}
        >
          <CurrencyFormat
            currency={props.value.currency}
            number={props.value.amount}
            decimals={0}
          />
        </Text>
      </div>
    </Tooltip>
  );
}
